import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast, ToastOptions } from 'react-toastify';
import { noAutoClose } from 'assets/data/config';
import parseErrorMessage from 'utils/parseErrorMessage';

export const keys = {
  incidentSchedule: 'incidentSchedule',
  userIncidentSchedule: 'userIncidentSchedule',
};

interface ScheduleData {
  incident_id: string;
  [key: string]: any;
}

interface DeleteScheduleParams {
  scheduleId: string;
  user_guid: string;
  incident_member_id: string;
}

// Helper function for error handling
const handleError = (error: any, errorMessage: string): void => {
  const fullErrorMessage = `${errorMessage}\n\n${parseErrorMessage(error)}\n\nIf you continue to experience this error, contact support@disastertech.com`;
  console.error(fullErrorMessage);
  toast.error(fullErrorMessage, noAutoClose as ToastOptions);
};

export const useGetAllSchedules = (incidentId: string, groupGuid: string) => {
  return useQuery({
    queryKey: [keys.incidentSchedule, incidentId],
    queryFn: async () => {
      const response = await axios(config.apiGateway.getAllScheduleToIncident, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    enabled: !!incidentId,
  });
};

export const useAddSchedule = (incidentId: string, groupGuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scheduleData: ScheduleData) => {
      const response = await axios(config.apiGateway.addScheduleToIncident, {
        method: 'POST',
        data: {
          schedule: {
            ...scheduleData,
            incident_id: incidentId,
          },
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentSchedule, incidentId],
      });
      // Invalidate user schedule query
      queryClient.invalidateQueries({
        queryKey: [
          keys.userIncidentSchedule,
          incidentId,
          variables.user_guid,
          variables.incident_member_id,
        ],
      });
      toast.success('Schedule added successfully');
    },
    onError: (error: any) => {
      if (
        error.response?.data ===
        '{"ERROR":"BAD REQUEST exception Error: User is already occupied at this selected schedule"}'
      ) {
        toast.error('User is already occupied at this selected schedule');
      } else {
        handleError(error, 'ERROR failed to add schedule to an incident');
      }
    },
  });
};

export const useDeleteSchedule = (incidentId: string, groupGuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      scheduleId,
      user_guid,
      incident_member_id,
    }: DeleteScheduleParams) => {
      const response = await axios(config.apiGateway.deleteScheduleToIncident, {
        method: 'POST',
        data: {
          scheduleId,
          user_guid,
          incident_member_id,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentSchedule, incidentId],
      });
      // Invalidate user schedule query
      queryClient.invalidateQueries({
        queryKey: [
          keys.userIncidentSchedule,
          incidentId,
          variables.user_guid,
          variables.incident_member_id,
        ],
      });
      toast.success('Schedule deleted successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR failed to delete schedule to an incident');
    },
  });
};

export const useGetUserSchedule = (
  incidentId: string,
  groupGuid: string,
  userId: string,
  incidentMemberId: string
) => {
  return useQuery({
    queryKey: [keys.userIncidentSchedule, incidentId, userId, incidentMemberId],
    queryFn: async () => {
      const response = await axios(
        config.apiGateway.getAllUserScheduleToIncident,
        {
          method: 'POST',
          data: {
            user_guid: userId,
            incident_id: incidentId,
            group_guid: groupGuid,
          },
        }
      );
      return response.data;
    },
    enabled: !!incidentId && !!userId,
  });
};
